
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { 
  TrendingUp, 
  Users, 
  Clock, 
  Target,
  BarChart3,
  Activity,
  Calendar,
  Award
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const AdvancedPerformanceCharts: React.FC = () => {
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');

  // Fetch real active learners count
  const { data: activeLearners = 0, isLoading: loadingLearners } = useQuery({
    queryKey: ['active-learners', timeRange],
    queryFn: async () => {
      const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data } = await supabase
        .from('module_activity_logs')
        .select('user_id')
        .gte('created_at', startDate.toISOString());

      // Count unique users
      const uniqueUsers = new Set(data?.map(log => log.user_id) || []);
      return uniqueUsers.size;
    }
  });

  // Fetch real learning progress data
  const { data: learningProgressData = [], isLoading: loadingProgress } = useQuery({
    queryKey: ['learning-progress', timeRange],
    queryFn: async () => {
      const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      const results = [];

      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dayStart = new Date(date.setHours(0, 0, 0, 0));
        const dayEnd = new Date(date.setHours(23, 59, 59, 999));

        // Count completed courses
        const { count: completed } = await supabase
          .from('course_assignments')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'completed')
          .gte('completion_date', dayStart.toISOString())
          .lte('completion_date', dayEnd.toISOString());

        // Count active enrollments
        const { count: inProgress } = await supabase
          .from('course_assignments')
          .select('id', { count: 'exact', head: true })
          .in('status', ['in_progress', 'active'])
          .gte('last_activity_date', dayStart.toISOString())
          .lte('last_activity_date', dayEnd.toISOString());

        // Count new enrollments
        const { count: started } = await supabase
          .from('course_assignments')
          .select('id', { count: 'exact', head: true })
          .gte('assigned_date', dayStart.toISOString())
          .lte('assigned_date', dayEnd.toISOString());

        results.push({
          date: dayStart.toISOString().split('T')[0],
          completed: completed || 0,
          inProgress: inProgress || 0,
          started: started || 0
        });
      }

      return results;
    }
  });

  // Fetch real certificate count
  const { data: certificateCount = 0 } = useQuery({
    queryKey: ['certificate-count', timeRange],
    queryFn: async () => {
      const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { count } = await supabase
        .from('certificates')
        .select('*', { count: 'exact', head: true })
        .gte('issue_date', startDate.toISOString());

      return count || 0;
    }
  });

  const isLoading = loadingLearners || loadingProgress;


  const timeRanges = [
    { id: '7d', label: '7 Days' },
    { id: '30d', label: '30 Days' },
    { id: '90d', label: '90 Days' }
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4">
                <div className="h-4 bg-muted rounded mb-2"></div>
                <div className="h-8 bg-muted rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-4 bg-muted rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="h-64 bg-muted rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-purple-600" />
            Advanced Performance Analytics
          </h2>
          <p className="text-muted-foreground">Comprehensive performance metrics and visualizations</p>
        </div>
        <div className="flex gap-2">
          {timeRanges.map((range) => (
            <Button
              key={range.id}
              variant={timeRange === range.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTimeRange(range.id as any)}
            >
              {range.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Learners</p>
                <p className="text-2xl font-bold">{activeLearners}</p>
                <p className="text-xs text-muted-foreground">Last {timeRange === '7d' ? '7 days' : timeRange === '30d' ? '30 days' : '90 days'}</p>
              </div>
              <Users className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Certificates Issued</p>
                <p className="text-2xl font-bold">{certificateCount}</p>
                <p className="text-xs text-muted-foreground">Last {timeRange === '7d' ? '7 days' : timeRange === '30d' ? '30 days' : '90 days'}</p>
              </div>
              <Award className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Grid */}
      {learningProgressData.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <BarChart3 className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">No performance data available yet</p>
            <p className="text-sm text-muted-foreground mt-2">
              Data will appear here once users start engaging with courses.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {/* Learning Progress Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Learning Progress Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={learningProgressData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="completed" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.6} name="Completed" />
                  <Area type="monotone" dataKey="inProgress" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} name="In Progress" />
                  <Area type="monotone" dataKey="started" stackId="1" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.6} name="Started" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdvancedPerformanceCharts;
